import React from 'react';
import {
  PartnerBlock,
  PartnerBlockProps,
  PartnerProfileData,
  PartnerProfileIndexes,
} from '@noths/polaris-client-ribbons-design-system';
import { CurrencyCode } from '@noths/polaris-dev-ts-types';

import { productModelToPartnerProduct } from 'src/content/blocks/partnerBlock/modelToUI';
import { PartnerModel } from 'src/content/blocks/partnerBlock/types';
import { CatalogServiceProductData } from 'src/services/catalog';
import { trackProductClick, trackProductImpression } from 'src/tracking/common/products';
import { transformProductModelToProductForTracking } from 'src/tracking/common/productTransformer';
import { trackItemsImpression } from 'src/tracking/common/trackItemsImpression';
import { trackLinkClick } from 'src/tracking/components/partnerBlock';
import { TrackingContext } from 'src/types/TrackingContext';
import { BlockContainer } from '../BlockContainer/BlockContainer';
import * as styles from './PartnerBlockBlock.style';

const hideNonPurchasableProducts = (products: CatalogServiceProductData[]) =>
  products.filter((product) => product.purchasable);

export interface PartnerBlockBlockProps extends Omit<PartnerBlockProps, 'partners'> {
  currency: CurrencyCode;
  pageType: string;
  partners: PartnerModel[];
  trackingTitle: string;
  trackingPosition?: number;
}

export const PartnerBlockBlock = ({
  currency,
  trackingPosition,
  trackingTitle,
  partners,
  pageType,
  ...restOfProps
}: PartnerBlockBlockProps) => {
  const trackingContext: TrackingContext = {
    trackingContentType: 'Partner block',
    trackingPosition,
    trackingTitle,
  };

  const partnersWithRenderedProducts = partners.map((partner) => ({
    ...partner,
    products: hideNonPurchasableProducts(partner.products),
  }));

  const partnersWithTransformedProducts = partnersWithRenderedProducts.map((partner) => ({
    ...partner,
    products: partner.products.map((product) => productModelToPartnerProduct(product, currency)),
  }));

  return (
    <BlockContainer wrapperStyles={styles.wrapper}>
      <PartnerBlock
        {...restOfProps}
        partners={partnersWithTransformedProducts}
        onProductVisible={({ partnerIndex }: { partnerIndex: number }) => {
          if (partnersWithRenderedProducts[partnerIndex].products.length > 0) {
            trackProductImpression({
              ...trackingContext,
              pageType,
              products: partnersWithRenderedProducts[partnerIndex].products.map(
                transformProductModelToProductForTracking,
              ),
            });
          }
        }}
        onProductClick={(
          e: React.MouseEvent<HTMLAnchorElement>,
          { partnerIndex, productIndex }: PartnerProfileIndexes,
        ) => {
          if (partnersWithRenderedProducts[partnerIndex].products.length > 0) {
            trackProductClick(e, {
              ...trackingContext,
              pageType,
              numberOfItems: partnersWithRenderedProducts[partnerIndex].products.length,
              product: transformProductModelToProductForTracking(
                partnersWithRenderedProducts[partnerIndex].products[productIndex],
              ),
              productIndex,
            });
          }
        }}
        onVisible={() =>
          trackItemsImpression({
            ...trackingContext,
            numberOfItems: partnersWithRenderedProducts.length,
          })
        }
        onPartnerClick={(
          e: React.MouseEvent<HTMLAnchorElement>,
          { partnerIndex }: { partnerIndex: number },
        ) => {
          trackLinkClick(e, {
            ...trackingContext,
            numberOfPartners: partnersWithRenderedProducts.length,
            partnerIndex,
            // get the partner name from the first product
            // assume both products are from the same partner
            partnerName: partnersWithRenderedProducts[partnerIndex].products[0].partner.name,
            partnerUrl: partnersWithRenderedProducts[partnerIndex].url,
          });
        }}
      />
    </BlockContainer>
  );
};

export type { PartnerProfileIndexes, PartnerProfileData };
